import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand } from "@/components/site/Blocks";
import { states } from "@/data/states";
import { buildHead } from "@/lib/seo-head";
import { GovernmentAffiliationNotice } from "@/components/site/GovernmentAffiliationNotice";
import heroQct from "@/assets/hero/hero-qualified-census-tracts.jpg.asset.json";

const HERO = {
  src: heroQct.url,
  alt: "Aerial dusk view of a city neighborhood with glowing gold qualified census tract polygons overlaid on a violet map grid, desk with demographic report sheets and a calculator in the foreground — HUD Qualified Census Tracts for LIHTC and Section 8 investors",
};

export const Route = createFileRoute("/qualified-census-tracts/")({
  head: () => {
    const url = "/qualified-census-tracts";
    const title = "2026 Qualified Census Tracts (QCT) by State | Simply Approved";
    const description =
      "HUD's 2026 Qualified Census Tracts by state — every LIHTC-boost tract and Section 8 demand hotspot in the states we serve.";
    return buildHead({
      title,
      description,
      url,
      keywords:
        "qualified census tracts 2026, QCT by state, LIHTC basis boost, HUD qualified census tract lookup, 130% qualified basis, QCT map, Section 8 voucher hotspots, LIHTC 30% basis boost tracts, HUD QCT designation, low income housing tax credit tracts, census tract poverty rate LIHTC",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Qualified Census Tracts", url },
      ],
        service: { name: "Qualified Census Tract Lookup" },
        image: HERO.src,
        imageAlt: HERO.alt,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "2026 Qualified Census Tracts — All States",
          description,
          creator: { "@type": "GovernmentOrganization", name: "U.S. Department of Housing and Urban Development" },
          license: "https://www.huduser.gov/portal/datasets/qct.html",
          temporalCoverage: "2026",
        },
      ],
    });
  },
  component: QctHub,
});

function QctHub() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="HUD data"
        title="Qualified Census Tracts (QCT)"
        subtitle="Pick a state to see every 2026 QCT, grouped by county. Tracts with 50%+ households under 60% AMI or 25%+ poverty — LIHTC 30% basis boost and Section 8 voucher concentration."
        primaryLabel="Start My Approval"
        primaryHref="/apply"
        secondaryLabel="See Today's Rates"
        secondaryHref="/rates"
        image={HERO}
      />
      <Section>
        <div className="mx-auto max-w-5xl">
          <h2 className="font-display text-2xl font-bold text-foreground">QCTs by state</h2>
          <ul className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {states.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/qualified-census-tracts/$state"
                  params={{ state: s.slug }}
                  className="block rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:border-brand hover:text-brand"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Section>
      <div className="container mx-auto max-w-7xl px-4 pb-10">
        <GovernmentAffiliationNotice />
      </div>

      <CTABand
        title="QCT purchase in the pipeline?"
        body="Send the address — we'll confirm QCT status, pull HUD FMR, and reply with DSCR pricing feedback."
      />
    </SiteShell>
  );
}
